/**
 * Device Data Transfer Object
 * Represents a normalized Device entity with validation
 */

export interface IDeviceData {
  uid?: string;
  id?: string;
  deviceId?: string;
  name?: string;
  deviceAppName?: string;
  deviceInfo?: {
    deviceAppName?: string;
    [key: string]: any;
  };
  gatewayUid?: string;
  gatewayId?: string;
}

export class DeviceDTO {
  readonly id: string;
  readonly name: string;
  readonly deviceAppName: string | null;
  readonly gatewayId: string | null;
  readonly deviceInfo: any;

  constructor(data: IDeviceData) {
    this.id = this.validateRequired(
      data.uid || data.id || data.deviceId,
      'id'
    );
    this.name = this.resolveName(data);
    this.deviceAppName = data.deviceInfo?.deviceAppName || data.deviceAppName || null;
    this.gatewayId = data.gatewayUid || data.gatewayId || null;
    this.deviceInfo = data.deviceInfo || null;
  }

  private validateRequired(value: any, fieldName: string): string {
    if (value === undefined || value === null || value === '') {
      throw new Error(`DeviceDTO: ${fieldName} is required`);
    }
    return String(value);
  }

  /**
   * Clear priority for device name resolution
   * Priority: deviceInfo.deviceAppName > deviceAppName > name > id
   */
  private resolveName(data: IDeviceData): string {
    if (data.deviceInfo?.deviceAppName) return data.deviceInfo.deviceAppName;
    if (data.deviceAppName) return data.deviceAppName;
    if (data.name) return data.name;
    return this.id;
  }

  /**
   * Create DTO from API response
   */
  static fromApiResponse(apiData: IDeviceData): DeviceDTO {
    return new DeviceDTO(apiData);
  }

  /**
   * Convert to plain object for store
   */
  toStoreEntity() {
    return {
      id: this.id,
      name: this.name,
      deviceAppName: this.deviceAppName,
      gatewayId: this.gatewayId,
      deviceInfo: this.deviceInfo,
    };
  }

  /**
   * Convert back to API format (for sending to backend)
   * Backend expects: uid, gatewayUid (not id, gatewayId)
   */
  toApiEntity() {
    return {
      uid: this.id,
      gatewayUid: this.gatewayId,
      deviceAppName: this.deviceAppName,
    };
  }

  /**
   * Convert store entity back to API format
   * Static helper for transforming arrays
   */
  static toApiEntity(storeEntity: any) {
    return {
      uid: storeEntity.id || storeEntity.uid,
      gatewayUid: storeEntity.gatewayId || storeEntity.gatewayUid,
      deviceAppName: storeEntity.deviceAppName,
    };
  }
}
